import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    siteName: 'OrbitMind',
    siteDescription: 'AI-Driven Automation Platform',
    maintenanceMode: false,
    userRegistration: true,
    emailNotifications: true,
    smsNotifications: false,
    backupFrequency: 'daily',
    maxUsers: 10000,
    sessionTimeout: 30,
    apiRateLimit: 1000
  });

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const tabs = [
    { id: 'general',       label: 'General',          icon: 'settings' },
    { id: 'security',      label: 'Security',          icon: 'shield' },
    { id: 'notifications', label: 'Notifications',     icon: 'notifications' },
    { id: 'database',      label: 'Database',          icon: 'storage' },
    { id: 'api',           label: 'API Settings',      icon: 'api' },
    { id: 'users',         label: 'User Management',   icon: 'group' }
  ];

  const Toggle = ({ value, onChange }) => (
    <button
      onClick={() => onChange(!value)}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 focus:outline-none"
      style={{ background: value ? 'linear-gradient(135deg, #13a4ec, #7c3aed)' : 'rgba(255,255,255,0.1)' }}
    >
      <span
        className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200"
        style={{ transform: value ? 'translateX(24px)' : 'translateX(4px)' }}
      />
    </button>
  );

  const Field = ({ label, children }) => (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
      {children}
    </div>
  );

  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm";
  const selectCls = "w-full px-4 py-2.5 rounded-xl border border-white/10 bg-[#0d1820] text-white outline-none focus:border-primary transition-colors text-sm";

  const ToggleRow = ({ title, desc, settingKey }) => (
    <div className="flex items-center justify-between p-4 rounded-xl border border-white/10" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
      <div>
        <p className="text-white font-semibold text-sm">{title}</p>
        <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
      </div>
      <Toggle value={settings[settingKey]} onChange={(v) => handleSettingChange(settingKey, v)} />
    </div>
  );

  const SectionCard = ({ title, icon, children }) => (
    <div className="rounded-2xl border border-white/10 p-6 mb-5" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
      <h3 className="text-base font-bold text-white mb-5 flex items-center gap-2">
        <span className="material-symbols-outlined text-primary text-lg">{icon}</span>
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen font-display" style={{ backgroundColor: '#101c22', color: '#fff' }}>
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Page header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
                <span className="material-symbols-outlined text-white text-xl">tune</span>
              </div>
              <h1 className="text-3xl font-extrabold text-white">Admin Settings</h1>
            </div>
            <p className="text-gray-400 text-sm ml-13">Configure system settings and platform preferences.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all text-sm font-medium">
              <span className="material-symbols-outlined text-base">arrow_back</span>
              Back to Admin
            </Link>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">

          {/* Sidebar tabs */}
          <aside className="lg:w-56 flex-shrink-0">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left"
                  style={
                    activeTab === tab.id
                      ? { background: 'linear-gradient(135deg, rgba(19,164,236,0.15), rgba(124,58,237,0.15))', color: '#13a4ec', border: '1px solid rgba(19,164,236,0.25)' }
                      : { color: '#9ca3af', border: '1px solid transparent' }
                  }
                >
                  <span className="material-symbols-outlined text-xl">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">

            {/* ── General ── */}
            {activeTab === 'general' && (
              <div>
                <SectionCard title="Platform Identity" icon="badge">
                  <Field label="Site Name">
                    <input type="text" value={settings.siteName} onChange={(e) => handleSettingChange('siteName', e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Site Description">
                    <textarea value={settings.siteDescription} onChange={(e) => handleSettingChange('siteDescription', e.target.value)} rows={3} className={inputCls + ' resize-none'} />
                  </Field>
                </SectionCard>
                <SectionCard title="Platform Controls" icon="toggle_on">
                  <ToggleRow title="Maintenance Mode" desc="Prevent user access while performing updates" settingKey="maintenanceMode" />
                  <ToggleRow title="User Registration" desc="Allow new users to create accounts" settingKey="userRegistration" />
                </SectionCard>
              </div>
            )}

            {/* ── Security ── */}
            {activeTab === 'security' && (
              <div>
                <SectionCard title="Session Management" icon="lock_clock">
                  <Field label="Session Timeout (minutes)">
                    <input type="number" value={settings.sessionTimeout} onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))} className={inputCls} />
                  </Field>
                </SectionCard>
                <SectionCard title="Two-Factor Authentication" icon="security">
                  {[
                    { label: 'Require 2FA for Admin Users', active: true },
                    { label: 'Require 2FA for All Users', active: false }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 rounded-xl border border-white/10" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                      <span className="text-gray-300 text-sm">{item.label}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${item.active ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-white/5 text-gray-500 border border-white/10'}`}>
                        {item.active ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  ))}
                </SectionCard>
                <SectionCard title="Password Policy" icon="password">
                  {['Minimum 8 characters', 'Require uppercase letters', 'Require numbers', 'Require special characters'].map((rule, i) => (
                    <label key={i} className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" defaultChecked={i < 3} className="w-4 h-4 rounded accent-primary" />
                      <span className="text-gray-300 text-sm">{rule}</span>
                    </label>
                  ))}
                </SectionCard>
              </div>
            )}

            {/* ── Notifications ── */}
            {activeTab === 'notifications' && (
              <div>
                <SectionCard title="Notification Channels" icon="campaign">
                  <ToggleRow title="Email Notifications" desc="Send system alerts and updates via email" settingKey="emailNotifications" />
                  <ToggleRow title="SMS Notifications" desc="Send critical alerts via SMS" settingKey="smsNotifications" />
                </SectionCard>
                <SectionCard title="Notification Types" icon="notifications_active">
                  {['System Alerts', 'User Activity', 'Performance Metrics', 'Security Events'].map((type, i) => (
                    <label key={i} className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" defaultChecked={i !== 2} className="w-4 h-4 rounded accent-primary" />
                      <span className="text-gray-300 text-sm">{type}</span>
                    </label>
                  ))}
                </SectionCard>
              </div>
            )}

            {/* ── Database ── */}
            {activeTab === 'database' && (
              <div>
                <SectionCard title="Backup Configuration" icon="backup">
                  <Field label="Backup Frequency">
                    <select value={settings.backupFrequency} onChange={(e) => handleSettingChange('backupFrequency', e.target.value)} className={selectCls}>
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </Field>
                </SectionCard>
                <SectionCard title="Database Status" icon="storage">
                  {[
                    { label: 'Connection Status', value: 'Connected', color: '#10b981' },
                    { label: 'Last Backup', value: '2 hours ago', color: '#fff' },
                    { label: 'Database Size', value: '2.4 GB', color: '#fff' },
                    { label: 'Active Connections', value: '47', color: '#fff' }
                  ].map((row, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <span className="text-gray-400 text-sm">{row.label}</span>
                      <span className="text-sm font-semibold" style={{ color: row.color }}>{row.value}</span>
                    </div>
                  ))}
                  <div className="flex gap-3 pt-2">
                    <button className="px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
                      Run Backup Now
                    </button>
                    <button className="px-4 py-2 rounded-xl text-sm font-bold text-gray-300 border border-white/10 hover:bg-white/5 transition-all">
                      Optimize Database
                    </button>
                  </div>
                </SectionCard>
              </div>
            )}

            {/* ── API ── */}
            {activeTab === 'api' && (
              <div>
                <SectionCard title="Rate Limiting" icon="speed">
                  <Field label="Rate Limit (requests per minute)">
                    <input type="number" value={settings.apiRateLimit} onChange={(e) => handleSettingChange('apiRateLimit', parseInt(e.target.value))} className={inputCls} />
                  </Field>
                </SectionCard>
                <SectionCard title="API Keys" icon="key">
                  {[
                    { label: 'Production API Key', value: 'pk_live_••••••••••••••••' },
                    { label: 'Test API Key', value: 'pk_test_••••••••••••••••' }
                  ].map((key, i) => (
                    <div key={i} className="flex items-center justify-between p-4 rounded-xl border border-white/10" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                      <div>
                        <p className="text-white font-semibold text-sm">{key.label}</p>
                        <p className="text-gray-500 text-xs font-mono mt-0.5">{key.value}</p>
                      </div>
                      <button className="text-sm font-semibold hover:opacity-80 transition-opacity" style={{ color: '#13a4ec' }}>Regenerate</button>
                    </div>
                  ))}
                </SectionCard>
                <SectionCard title="API Usage (Last 24h)" icon="analytics">
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { value: '12,847', label: 'Total Requests', color: '#13a4ec' },
                      { value: '99.2%', label: 'Success Rate', color: '#10b981' },
                      { value: '145ms', label: 'Avg Response', color: '#f59e0b' }
                    ].map((stat, i) => (
                      <div key={i} className="text-center p-4 rounded-xl border border-white/10" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                        <p className="text-2xl font-extrabold mb-1" style={{ color: stat.color }}>{stat.value}</p>
                        <p className="text-gray-500 text-xs">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            )}

            {/* ── Users ── */}
            {activeTab === 'users' && (
              <div>
                <SectionCard title="User Limits" icon="manage_accounts">
                  <Field label="Maximum Users">
                    <input type="number" value={settings.maxUsers} onChange={(e) => handleSettingChange('maxUsers', parseInt(e.target.value))} className={inputCls} />
                  </Field>
                </SectionCard>
                <SectionCard title="User Statistics" icon="bar_chart">
                  {[
                    { label: 'Total Users', value: '2,847', color: '#fff' },
                    { label: 'Active Users', value: '2,156', color: '#10b981' },
                    { label: 'Admin Users', value: '12', color: '#fff' },
                    { label: 'Suspended Users', value: '23', color: '#ef4444' }
                  ].map((row, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <span className="text-gray-400 text-sm">{row.label}</span>
                      <span className="text-sm font-bold" style={{ color: row.color }}>{row.value}</span>
                    </div>
                  ))}
                </SectionCard>
                <SectionCard title="Default User Permissions" icon="verified_user">
                  {['Create Workflows', 'Deploy Services', 'Access Analytics', 'Manage Team'].map((perm, i) => (
                    <label key={i} className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" defaultChecked={i < 2} className="w-4 h-4 rounded accent-primary" />
                      <span className="text-gray-300 text-sm">{perm}</span>
                    </label>
                  ))}
                </SectionCard>
              </div>
            )}

            {/* Save / Reset bar */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-2">
              <button
                onClick={() => setSettings({ siteName: 'OrbitMind', siteDescription: 'AI-Driven Automation Platform', maintenanceMode: false, userRegistration: true, emailNotifications: true, smsNotifications: false, backupFrequency: 'daily', maxUsers: 10000, sessionTimeout: 30, apiRateLimit: 1000 })}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-400 border border-white/10 hover:bg-white/5 hover:text-white transition-all"
              >
                Reset to Defaults
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)', boxShadow: saved ? '0 0 20px rgba(19,164,236,0.4)' : 'none' }}
              >
                {saved ? (
                  <><span className="material-symbols-outlined text-base">check_circle</span> Saved!</>
                ) : (
                  <><span className="material-symbols-outlined text-base">save</span> Save Changes</>
                )}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
